import { apiVisitEngine, queryClient } from '@/shared'
import type { IApiResponse } from '@/shared/types/api'
import { getAxiosErrorData } from '@/shared/utils/axios-error'
import { toaster } from '@/shared/ui'
import type {
  IQuestion,
  IQuestionnaireErrorResponse,
  IQuestionnaireResponse,
  IQuestionnaireSubmit,
  IQuestionnaireUpdatePayload,
  ISurveyHistoryDetail,
  ISurveyHistoryListItem,
  IVisitQuestionnaireItem,
} from '../types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/query-keys'

export const questionnaireApi = {
  getQuestionnaire: async (gender?: 'MALE' | 'FEMALE', donorId?: string) => {
    const response = await apiVisitEngine.get<IApiResponse<IQuestion[]>>(
      `/v1/questionnaire`,
      {
        params: {
          gender,
          donor_id: donorId,
        },
      },
    )
    return response.data
  },

  postQuestionnaire: async (data: IQuestionnaireSubmit) => {
    const response = await apiVisitEngine.post<
      IApiResponse<IQuestionnaireResponse>
    >(`/v1/questionnaire/submit`, data, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  // Per-visit questionnaire (structure + previously stored answers).
  getQuestionnaireByVisit: async (visitId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<IVisitQuestionnaireItem[]>
    >(`/v1/questionnaire/visits/${visitId}`)
    return response.data
  },

  // Edit answers for an already-completed per-visit questionnaire.
  putQuestionnaireByVisit: async (
    visitId: string,
    payload: IQuestionnaireUpdatePayload,
  ) => {
    const response = await apiVisitEngine.put<
      IApiResponse<IQuestionnaireResponse>
    >(`/v1/questionnaire/visits/${visitId}`, payload, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  // Past surveys for a donor (one per visit).
  getSurveyHistory: async (donorId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<ISurveyHistoryListItem[]>
    >(`/v1/questionnaire/donors/${donorId}/survey-history`)
    return response.data
  },

  // A single past survey with its stored answers and decision.
  getSurveyHistoryDetail: async (historyId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<ISurveyHistoryDetail>
    >(`/v1/questionnaire/survey-history/${historyId}`)
    return response.data
  },
}

export const useGetQuestionnaireQuery = (
  gender?: 'MALE' | 'FEMALE',
  donorId?: string,
) => {
  return useQuery({
    queryKey: queryKeys.questionnaire.all({ gender, donorId }),
    queryFn: () => questionnaireApi.getQuestionnaire(gender, donorId),
    enabled: !!gender,
  })
}

export const useGetQuestionnaireByVisitQuery = (
  visitId: string,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: queryKeys.questionnaire.byVisit(visitId),
    queryFn: () => questionnaireApi.getQuestionnaireByVisit(visitId),
    enabled: enabled && !!visitId,
  })

export const usePutQuestionnaireByVisitMutation = (
  visitId: string,
  donorId?: string,
) => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: IQuestionnaireUpdatePayload) =>
      questionnaireApi.putQuestionnaireByVisit(visitId, payload),
    onSuccess: () => {
      toaster.success({ title: t('donor_detail.medical_survey.save_success') })
      queryClient.invalidateQueries({
        queryKey: queryKeys.questionnaire.byVisit(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.detail(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId ?? ''),
      })
    },
    onError: (error) => {
      const body = getAxiosErrorData<IQuestionnaireErrorResponse>(error)

      queryClient.invalidateQueries({
        queryKey: queryKeys.questionnaire.byVisit(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.detail(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId ?? ''),
      })

      if (body?.errors[0]?.message) {
        body.errors.forEach((err) => {
          toaster.error({ title: err.message })
        })
        return
      }

      toaster.error({ title: t('donor_detail.medical_survey.save_error') })
    },
  })
}

export const useGetSurveyHistoryQuery = (
  donorId: string,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: queryKeys.questionnaire.surveyHistory(donorId),
    queryFn: () => questionnaireApi.getSurveyHistory(donorId),
    enabled: enabled && !!donorId,
  })

export const useGetSurveyHistoryDetailQuery = (historyId: string | null) =>
  useQuery({
    queryKey: queryKeys.questionnaire.surveyHistoryDetail(historyId ?? ''),
    queryFn: () => questionnaireApi.getSurveyHistoryDetail(historyId as string),
    enabled: !!historyId,
  })

export const usePostQuestionnaireMutation = (donorId?: string) => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (data: IQuestionnaireSubmit) =>
      questionnaireApi.postQuestionnaire(data),
    onSuccess: (_data, variables) => {
      toaster.success({ title: t('donor_detail.medical_survey.save_success') })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.detail(variables.visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId ?? ''),
      })
    },
    onError: (error, variables) => {
      const body = getAxiosErrorData<IQuestionnaireErrorResponse>(error)

      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.detail(variables.visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId ?? ''),
      })

      if (body?.errors[0]?.message) {
        body.errors.forEach((error) => {
          toaster.error({ title: error.message })
        })
        return
      }

      toaster.error({ title: t('donor_detail.medical_survey.save_error') })
    },
  })
}
